import { createContext, useContext, useState, useEffect } from 'react'
import { mockUsersByRole } from '../data/mockUsers'
import { mockCoachSignals } from '../data/mockCoachSignals'

const API = import.meta.env.VITE_API_URL || ''
const BACKEND_ENABLED = import.meta.env.VITE_BACKEND_ENABLED === 'true' || Boolean(API)
const DEMO_MODE = !BACKEND_ENABLED
const PREVIEW_LOGIN_ENABLED = import.meta.env.VITE_ARGUS_PREVIEW_LOGIN === 'true'
const SERVER_PREVIEW_PROFILE_KEYS = new Set([
  'onboarding',
  'athlete1',
  'coach',
  'athlete2',
  'athlete3',
  'athlete4',
])

const AuthContext = createContext(null)

async function parseApiResponse(response, fallbackMessage) {
  const text = await response.text()
  let data = null

  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    const error = new Error(
      response.ok
        ? `${fallbackMessage}: invalid response`
        : `${fallbackMessage} (HTTP ${response.status})`,
    )
    error.code = 'ARGUS_INVALID_RESPONSE'
    error.status = response.status
    throw error
  }

  if (!response.ok) {
    const error = new Error(data?.error ?? `${fallbackMessage} (HTTP ${response.status})`)
    error.status = response.status
    throw error
  }

  return data
}

async function requestCurrentUser() {
  const response = await fetch(`${API}/api/me`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  return parseApiResponse(response, 'Unable to restore authenticated profile')
}

function workspaceRoleFromEntryRole(roleKey) {
  const normalized = String(roleKey ?? '')
    .trim()
    .toLowerCase()

  if (normalized === 'coach' || normalized === 'trainer') return 'coach'
  if (normalized === 'club' || normalized === 'club_admin' || normalized === 'team_admin') {
    return 'club'
  }
  if (normalized === 'partner' || normalized === 'sponsor') return 'sponsor'
  return 'player'
}

function withLocalSignals(workspaceRole, fixture) {
  if (!fixture) return fixture
  if (workspaceRole === 'coach') {
    return {
      ...fixture,
      coach_signals: mockCoachSignals,
    }
  }
  return fixture
}

function localPreviewFixture(profileKey) {
  const key = String(profileKey ?? '')
    .trim()
    .toLowerCase()
  const roleByKey = {
    athlete1: 'player',
    athlete: 'player',
    player: 'player',
    coach: 'coach',
    club: 'club',
    partner: 'sponsor',
    sponsor: 'sponsor',
  }
  const role = roleByKey[key] ?? null
  const fixture = role ? withLocalSignals(role, mockUsersByRole[role]) : null
  if (!fixture) return null

  return {
    ...fixture,
    preview: true,
    preview_profile_key: profileKey,
    preview_local_fallback: true,
    preview_local_direct_entry: true,
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(DEMO_MODE ? false : true)

  useEffect(() => {
    if (DEMO_MODE) return

    requestCurrentUser()
      .then((data) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  async function login(email, password) {
    if (DEMO_MODE) throw new Error('Use demo login buttons')

    const res = await fetch(`${API}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    })
    const data = await parseApiResponse(res, 'Login failed')
    setUser(data)
    return data
  }

  async function requestPreviewProfile(profileKey = 'athlete1') {
    const res = await fetch(`${API}/api/preview-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ profile_key: profileKey }),
    })
    return parseApiResponse(res, 'Preview login failed')
  }

  async function loginPreviewProfile(profileKey = 'athlete1') {
    if (!PREVIEW_LOGIN_ENABLED || DEMO_MODE) {
      throw new Error('ARGUS preview login is unavailable')
    }

    const normalizedProfileKey = String(profileKey ?? '')
      .trim()
      .toLowerCase()
    const fixture = localPreviewFixture(normalizedProfileKey)

    try {
      // Establish the real preview HttpOnly session first. The visual fixture can enrich
      // the review surface, but it must never replace the authenticated server boundary.
      await requestPreviewProfile(normalizedProfileKey)
      const serverUser = await requestCurrentUser()
      const workspaceRole = workspaceRoleFromEntryRole(
        serverUser?.role ?? serverUser?.roles?.[0] ?? fixture?.role,
      )
      const enrichedFixture = fixture ? withLocalSignals(workspaceRole, fixture) : null
      const data = {
        ...(enrichedFixture ?? {}),
        ...serverUser,
        preview: true,
        preview_profile_key: normalizedProfileKey,
        preview_local_fallback: false,
        preview_local_direct_entry: false,
      }
      setUser(data)
      return data
    } catch (err) {
      // Server-backed preview identities must fail closed. Otherwise the UI appears signed in
      // while profile/onboarding APIs correctly reject the missing session with HTTP 401.
      if (SERVER_PREVIEW_PROFILE_KEYS.has(normalizedProfileKey) || !fixture) throw err

      // Local-only fixtures remain available for presentation surfaces that intentionally have
      // no authoritative backend identity. Server-backed mutations stay unavailable there.
      setUser(fixture)
      return fixture
    }
  }

  async function registerPreviewAccount(selectedRoleKey = null) {
    if (!PREVIEW_LOGIN_ENABLED || DEMO_MODE) {
      throw new Error('ARGUS preview registration is unavailable')
    }

    // Registration is an authoritative lifecycle action: require the server to create
    // the compatibility account, normalized person/self-link, onboarding instance,
    // external identity, and HttpOnly session before the UI treats the user as signed in.
    const res = await fetch(`${API}/api/preview-register`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
    const data = await parseApiResponse(res, 'Preview registration failed')
    const registeredUser = {
      ...data,
      preview_selected_role: selectedRoleKey || 'athlete',
    }
    setUser(registeredUser)
    return registeredUser
  }

  async function registerPreviewCampaignOwnerAccount() {
    if (!PREVIEW_LOGIN_ENABLED || DEMO_MODE) {
      throw new Error('ARGUS campaign owner preview registration is unavailable')
    }

    const res = await fetch(`${API}/api/preview-campaign-owner-register`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
    const data = await parseApiResponse(res, 'Campaign owner preview registration failed')
    setUser(data)
    return data
  }

  async function registerPreviewCampaignReviewerAccount() {
    if (!PREVIEW_LOGIN_ENABLED || DEMO_MODE) {
      throw new Error('ARGUS campaign reviewer preview registration is unavailable')
    }

    const res = await fetch(`${API}/api/preview-campaign-reviewer-register`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
    const data = await parseApiResponse(res, 'Campaign reviewer preview registration failed')
    setUser(data)
    return data
  }

  function loginAsRole(role) {
    const mockUser = withLocalSignals(role, mockUsersByRole[role])
    if (mockUser) setUser(mockUser)
    return mockUser
  }

  async function logout() {
    if (!DEMO_MODE) {
      await fetch(`${API}/api/logout`, { method: 'POST', credentials: 'include' }).catch(() => {})
    }
    setUser(null)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginPreviewProfile,
        registerPreviewAccount,
        registerPreviewCampaignOwnerAccount,
        registerPreviewCampaignReviewerAccount,
        previewLoginEnabled: PREVIEW_LOGIN_ENABLED && !DEMO_MODE,
        demoMode: DEMO_MODE,
        loginAsRole,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
